import {
  BusinessAddressForm,
  type BusinessAddressRef,
  type Country,
} from '@seek/hirer-business-address';
import { Box } from 'braid-design-system';
import React, {
  type RefObject,
  type FocusEvent,
  useEffect,
  type FormEvent,
  useCallback,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Element } from 'react-scroll';

import { EVENT_NAME_INTERACTION } from 'src/app/analytics';
import { COUNTRY_ID_AUSTRALIA } from 'src/app/constants/country';
import { SECTION_BUSINESS_ADDRESS } from 'src/app/constants/pageSectionName';
import getTrackingAdapter from 'src/app/dataAnalytics';
import { BUSINESS_ADDRESS_PRESSED } from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { businessAddress as businessAddressDispatcher } from 'src/app/state/dispatcher';
import { countryList } from 'src/app/taxonomies/countryList';
import { sendLinkEvent } from 'src/app/utils/analytics';
import { log } from 'src/app/utils/logging';
import { useConfig } from 'src/app/views/hooks/useConfig';
import { OverlayLoader } from 'src/app/views/shared/OverlayLoader';
import type AppState from 'src/types/appState';
import type { TealiumEventData } from 'src/types/trackingData';
const ANALYTICS_LINK_NAME_ADDRESS_LINES =
  'ecaja-business-address-lines-clicked';
const ANALYTICS_OBJECTINTERACTION_ADDRESS_LINES = 'address-lines';
const ANALYTICS_LINK_NAME_ADDRESS1 = 'ecaja-business-address1-clicked';
const ANALYTICS_OBJECTINTERACTION_ADDRESS1 = 'address-line-1';
const ANALYTICS_LINK_NAME_ADDRESS2 = 'ecaja-business-address2-clicked';
const ANALYTICS_OBJECTINTERACTION_ADDRESS2 = 'address-line-2';
const ANALYTICS_LINK_NAME_SUBURB = 'ecaja-business-suburb-clicked';
const ANALYTICS_OBJECTINTERACTION_SUBURB = 'suburb';
const ANALYTICS_LINK_NAME_POSTCODE = 'ecaja-business-postcode-clicked';
const ANALYTICS_OBJECTINTERACTION_POSTCODE = 'postcode';
const ANALYTICS_LINK_NAME_STATE = 'ecaja-business-state-clicked';
const ANALYTICS_OBJECTINTERACTION_STATE = 'state';
interface Props {
  businessAddressRef: RefObject<BusinessAddressRef>;
  hasLoadingOverlay?: boolean;
}

export const BusinessAddressSection = ({
  businessAddressRef,
  hasLoadingOverlay,
}: Props) => {
  const {
    addressLine1,
    addressLine2,
    suburb,
    state,
    postcode,
    country,
    hirerNation,
    businessAddressCountry,
    tealium,
  } = useSelector(
    (appState: AppState) => ({
      country: appState.session.postalAddress?.country,
      hirerNation: appState.session.hirerAccount.nation,
      businessAddressCountry: appState.app.businessAddress.countryId,
      tealium: appState.tealium,
      state: appState.app.businessAddress.state,
      suburb: appState.app.businessAddress.suburb,
      addressLine1: appState.app.businessAddress.addressLine1,
      addressLine2: appState.app.businessAddress.addressLine2,
      postcode: appState.app.businessAddress.postcode,
    }),
    shallowEqual,
  );

  const { getLanguage } = useConfig();
  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);

  const dispatch = useDispatch();

  // This hook is used to set countryId on mount, to ensure it is in the store by the time the user clicks "Post my ad".
  useEffect(() => {
    if (!businessAddressCountry) {
      const countryId =
        countryList.find((item) => item.text === hirerNation)?.value ??
        COUNTRY_ID_AUSTRALIA;

      dispatch(businessAddressDispatcher('countryId', countryId));
    }
  }, [businessAddressCountry, dispatch, hirerNation]);

  const objectInteracted: Record<string, string> = {
    addressLine1: 'address-line-1',
    addressLine2: 'address-line-2',
  };

  const onBlur = (
    event: FocusEvent<HTMLInputElement | HTMLSelectElement>,
  ): void => {
    const { id } = event.target;

    if (isPageAnalyticsEnabled) {
      const eventData = {
        eventName: BUSINESS_ADDRESS_PRESSED,
        objectInteraction: objectInteracted[id] ?? id.toLowerCase(),
      };

      getTrackingAdapter.sendLinkEvent(eventData);
    } else {
      const eventData: TealiumEventData = getEventData(id);
      if (eventData) {
        sendLinkEvent(tealium, eventData);
      }
    }
  };

  const onChange = useCallback(
    (event: FormEvent<HTMLInputElement | HTMLSelectElement>): void => {
      const { id, value } = event.target as HTMLInputElement;
      // @TODO: Remove CAJA's use of "addressLine1" and "addressLine2" and
      // instead implement and always use "addressLines" in redux.
      // The below is a patch fix where we try and coerce the addressLines
      // to fit into the existing addressLine1 and addressLine2 structure
      if (id === 'addressLines') {
        dispatch(businessAddressDispatcher('addressLine1', value));
        dispatch(businessAddressDispatcher('addressLine2', ''));
        return;
      }
      dispatch(businessAddressDispatcher(id, value));
    },
    [dispatch],
  );

  const getEventData = (id: string): TealiumEventData => {
    let linkName = '';
    let objectInteraction = '';

    switch (id) {
      case 'addressLines':
        linkName = ANALYTICS_LINK_NAME_ADDRESS_LINES;
        objectInteraction = ANALYTICS_OBJECTINTERACTION_ADDRESS_LINES;
        break;
      case 'addressLine1':
        linkName = ANALYTICS_LINK_NAME_ADDRESS1;
        objectInteraction = ANALYTICS_OBJECTINTERACTION_ADDRESS1;
        break;
      case 'addressLine2':
        linkName = ANALYTICS_LINK_NAME_ADDRESS2;
        objectInteraction = ANALYTICS_OBJECTINTERACTION_ADDRESS2;
        break;
      case 'suburb':
        linkName = ANALYTICS_LINK_NAME_SUBURB;
        objectInteraction = ANALYTICS_OBJECTINTERACTION_SUBURB;
        break;
      case 'state':
        linkName = ANALYTICS_LINK_NAME_STATE;
        objectInteraction = ANALYTICS_OBJECTINTERACTION_STATE;
        break;
      case 'postCode':
        linkName = ANALYTICS_LINK_NAME_POSTCODE;
        objectInteraction = ANALYTICS_OBJECTINTERACTION_POSTCODE;
        break;
      default:
        log.error('BusinessAddress | Unknown business address field id', {
          id,
        });
    }
    const eventData: TealiumEventData = {
      linkName,
      eventName: [EVENT_NAME_INTERACTION, linkName],
      objectInteraction,
    };
    return eventData;
  };

  const isLoadingBusinessAddress = !country;
  return (
    <Element name={SECTION_BUSINESS_ADDRESS}>
      <Box position="relative" style={{ minHeight: '508px' }}>
        {hasLoadingOverlay && isLoadingBusinessAddress ? (
          <OverlayLoader />
        ) : (
          <BusinessAddressForm
            language={getLanguage()}
            businessAddressRef={businessAddressRef}
            onSubmit={() => {}}
            onBlur={onBlur}
            onChange={onChange}
            address={{
              countryCode: hirerNation.toUpperCase() as Country,
              addressLines: [addressLine1 || '', addressLine2 || ''].filter(
                Boolean,
              ),
              suburb: suburb || '',
              state,
              postCode: postcode,
            }}
          />
        )}
      </Box>
    </Element>
  );
};
